import * as React from 'react';
import {
    Button,
    Form,
    Upload,
    message,
    Col,
    Modal,
} from 'antd';
import {
    UploadOutlined,
    FileImageOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import GenericHoc from 'components/common/generic-hoc';
import { HocOptions } from 'components/common/generic-hoc.types';
import { ReduxStore } from 'reducers/redux.types';
import { StylesProps } from '../../../../theme/jss-types';
import { FormInstance } from 'antd/lib/form';
import { ImageListUploadStyles } from './image-list-upload.styles';

interface Props extends StylesProps<typeof ImageListUploadStyles> {
    form: FormInstance;
    params: any;
    classes: any;
    isPieceLevel: boolean;
}

const ImageListUpload = (props: Props) => {
    const { t } = useTranslation();
    const {
        form,
        classes,
        params,
        isPieceLevel,
    } = props;

    const { pieceIndex, key, disabledFields = {} } = params;
    const maxFiles = 5;

    const formFieldKey = isPieceLevel ? `piece_imageList_${pieceIndex}` : key;
    const isDisabled = !!disabledFields[formFieldKey || ''] || !!disabledFields.proofImageList;
    const [fileList, setFileList] = React.useState<any[]>([]);
    const [previewVisible, setPreviewVisible] = React.useState(false);
    const [previewImage, setPreviewImage] = React.useState('');
    const [previewTitle, setPreviewTitle] = React.useState('');

    const handleChange = (info: any) => {
        const file = info?.file;
        const isValidSize = file && file?.size / 1024 / 1024 < 2;

        if (!isValidSize && file?.status !== 'removed') {
            message.error('File must be smaller than 2MB!');
            return;
        }

        let fileListToSet = info?.fileList || [];
        const newFilesCount = fileListToSet.length;

        if (newFilesCount > maxFiles) {
            message.warning(`Only the first ${maxFiles} files will be uploaded`);
            fileListToSet = fileListToSet.slice(0, maxFiles);
        }

        fileListToSet = fileListToSet.map((item: any) => {
            const updatedItem = { ...item };
            if (updatedItem.originFileObj && !updatedItem.url && !updatedItem.preview) {
                updatedItem.preview = URL.createObjectURL(updatedItem.originFileObj);
                updatedItem.thumbUrl = updatedItem.preview;
            }

            if (updatedItem.status !== 'removed' && updatedItem.status !== 'error') {
                updatedItem.status = 'done';
            }

            return updatedItem;
        });

        setFileList(fileListToSet);
        form.setFieldsValue({
            [formFieldKey]: fileListToSet,
        });
    };

    const handleRemove = (file: any) => {
        const updatedList = fileList?.filter((f: any) => f?.uid !== file?.uid);
        setFileList(updatedList);
        form.setFieldsValue({
            [formFieldKey]: updatedList,
        });
    };

    const handlePreview = async (file: any) => {
        if (file.url) {
            setPreviewImage(file.url);
            setPreviewVisible(true);
            setPreviewTitle(file.name || 'Image Preview');
        } else if (file.originFileObj) {
            const reader = new FileReader();
            reader.onload = (e) => {
                setPreviewImage(e.target?.result as string);
                setPreviewVisible(true);
                setPreviewTitle(file.name || 'Image Preview');
            };
            reader.readAsDataURL(file.originFileObj);
        }
    };

    const handleCancelPreview = () => {
        setPreviewVisible(false);
    };

    return (
        <Col span={24} className={classes.container}>
            <div className={classes.sectionTitle}>
                <FileImageOutlined className={classes.fileImageIcon} />
                <span className={classes.uploadText}>{t('Proof Images')}</span>
            </div>
            <Form.Item name={formFieldKey}>
                <div>
                    <Upload
                        name="file"
                        fileList={fileList}
                        onChange={handleChange}
                        onRemove={handleRemove}
                        onPreview={handlePreview}
                        disabled={isDisabled}
                        multiple
                        accept="image/jpeg,image/jpg,image/png"
                        beforeUpload={(file) => {
                            const isValidType = ['image/jpeg', 'image/jpg', 'image/png'].includes(file.type);
                            const isLt2M = file.size / 1024 / 1024 < 2;

                            if (!isValidType) {
                                message.error('Only JPG, JPEG, and PNG files are allowed!');
                                return false;
                            }

                            if (!isLt2M) {
                                message.error('File must be smaller than 2MB!');
                                return false;
                            }

                            return false;
                        }}
                        listType="picture"
                    >
                        <Button
                            disabled={isDisabled || (fileList && fileList.length >= maxFiles)}
                            icon={<UploadOutlined />}
                        >
                            {t('Upload Images')}
                        </Button>
                    </Upload>
                    <div className={classes.helpText}>
                        {`Maximum ${maxFiles} files can be uploaded. Formats supported: jpg, png, jpeg.`}
                    </div>
                </div>
            </Form.Item>

            <Modal
                visible={previewVisible}
                title={previewTitle}
                footer={null}
                onCancel={handleCancelPreview}
                width="60%"
            >
                <img alt="preview" style={{ width: '100%' }} src={previewImage} />
            </Modal>
        </Col>
    );
};

const mapStateToProps = (state: ReduxStore) => {
    const { master } = state;
    const { config } = master;

    return {
        config,
        master,
    };
};

const hocConfig: HocOptions = {
    connectRedux: {
        useRedux: true,
        mapStateToProps,
    },
    connectJss: {
        useJss: true,
        styleSheet: ImageListUploadStyles,
    },
};

export default GenericHoc(hocConfig)(ImageListUpload);

